import { defineComponent, toRefs, ref, watch, computed, resolveComponent, openBlock, createBlock, mergeProps, withCtx, createVNode } from "vue";
import { getOptionInfos, getKeysFromValue, getLeafOptionKeys } from "./utils.js";
import Trigger from "../trigger/index.js";
import SelectView from "../_components/select-view/select-view.js";
import CascaderPanel from "./cascader-panel.js";
import CascaderSearchPanel from "./cascader-search-panel.js";
import { isArray } from "../_utils/is.js";
import { useSelectedPath } from "./hooks/use-selected-path.js";
import { getKeyDownHandler, CODE } from "../_utils/keyboard.js";
import _export_sfc from "../_virtual/plugin-vue_export-helper";
const _sfc_main = defineComponent({
  name: "Cascader",
  components: {
    Trigger,
    SelectView,
    CascaderPanel,
    CascaderSearchPanel
  },
  inheritAttrs: false,
  props: {
    pathMode: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    modelValue: {
      type: [String, Number, Array]
    },
    defaultValue: {
      type: [String, Number, Array],
      default: (props) => props.multiple ? [] : props.mode === "value" ? "" : void 0
    },
    options: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    allowSearch: {
      type: Boolean,
      default: false
    },
    allowClear: {
      type: Boolean,
      default: false
    },
    inputValue: {
      type: String,
      default: void 0
    },
    defaultInputValue: {
      type: String,
      default: ""
    },
    popupVisible: {
      type: Boolean,
      default: void 0
    },
    expandTrigger: {
      type: String,
      default: "click"
    },
    defaultPopupVisible: {
      type: Boolean,
      default: false
    },
    placeholder: String,
    filterOption: {
      type: Function
    },
    popupContainer: {
      type: [String, Object]
    },
    formatLabel: {
      type: Function
    },
    triggerProps: {
      type: Object
    },
    onChange: {
      type: [Function, Array]
    },
    onInputValueChange: {
      type: [Function, Array]
    },
    onPopupVisibleChange: {
      type: [Function, Array]
    },
    onClear: { type: [Function, Array] },
    onSearch: {
      type: [Function, Array]
    }
  },
  emits: [
    "update:modelValue",
    "change",
    "inputValueChange",
    "clear",
    "search",
    "popupVisibleChange",
    "focus",
    "blur"
  ],
  setup(props, { emit }) {
    const { options } = toRefs(props);
    const _value = ref(props.defaultValue);
    const _inputValue = ref(props.defaultInputValue);
    const _popupVisible = ref(props.defaultPopupVisible);
    const optionInfos = ref([]);
    const totalLevel = ref(1);
    const leafOptionSet = new Set();
    const leafOptionMap = new Map();
    const leafOptionValueMap = new Map();
    watch(options, (_options) => {
      leafOptionSet.clear();
      leafOptionMap.clear();
      leafOptionValueMap.clear();
      optionInfos.value = getOptionInfos(props.options, {
        leafOptionSet,
        leafOptionMap,
        leafOptionValueMap,
        totalLevel
      });
    }, {
      immediate: true
    });
    const computedKeys = computed(() => {
      var _a;
      return getKeysFromValue((_a = props.modelValue) != null ? _a : _value.value, {
        pathMode: props.pathMode,
        leafOptionMap,
        leafOptionValueMap
      });
    });
    const computedInputValue = computed(() => {
      var _a;
      return (_a = props.inputValue) != null ? _a : _inputValue.value;
    });
    const computedPopupVisible = computed(() => {
      var _a;
      return (_a = props.popupVisible) != null ? _a : _popupVisible.value;
    });
    const filteredLeafOptions = computed(() => Array.from(leafOptionSet).filter((item) => {
      var _a, _b, _c, _d;
      return (_d = (_a = props.filterOption) == null ? void 0 : _a.call(props, computedInputValue.value, item)) != null ? _d : (_c = item.label) == null ? void 0 : _c.toLocaleLowerCase().includes((_b = computedInputValue.value) == null ? void 0 : _b.toLocaleLowerCase());
    }));
    const updateValue = (options2) => {
      let value;
      if (!options2) {
        if (!props.pathMode) {
          value = "";
        }
        setSelectedPath();
        setActiveNode();
      } else if (isArray(options2)) {
        value = options2.map((item) => {
          if (!props.pathMode) {
            return item.value;
          }
          return item.path.map((item2) => item2.value);
        });
      } else if (!props.pathMode) {
        value = options2.value;
      } else {
        value = options2.path.map((item) => item.value);
      }
      _value.value = value;
      emit("update:modelValue", value);
      emit("change", value);
    };
    const handlePopupVisibleChange = (visible) => {
      if (computedPopupVisible.value !== visible) {
        _popupVisible.value = visible;
        emit("popupVisibleChange", visible);
      }
    };
    const handleRemove = (key) => {
      if (props.multiple) {
        const option = leafOptionMap.get(key);
        if (option) {
          selectMultiple(option, false);
        }
      }
    };
    const selectSingle = (option) => {
      updateValue(option);
      handlePopupVisibleChange(false);
    };
    const selectMultiple = (option, checked) => {
      const leafOptionKeys = getLeafOptionKeys(option);
      const newKeys = checked ? computedKeys.value.concat(leafOptionKeys.filter((item) => !computedKeys.value.includes(item))) : computedKeys.value.filter((item) => !leafOptionKeys.includes(item));
      updateValue(newKeys.map((key) => leafOptionMap.get(key)));
      handleInputValueChange("", "optionChecked");
    };
    const handleClickOption = (option, checked) => {
      if (props.multiple) {
        selectMultiple(option, checked != null ? checked : true);
      } else {
        selectSingle(option);
      }
    };
    const getOptionLabel = (option) => {
      return option.path.map((item) => item.label).join(" / ");
    };
    const handleInputValueChange = (value, reason) => {
      if (value !== computedInputValue.value) {
        if (reason === "manual" && !computedPopupVisible.value) {
          _popupVisible.value = true;
          emit("popupVisibleChange", true);
        }
        _inputValue.value = value;
        emit("inputValueChange", value);
        if (props.allowSearch) {
          emit("search", value);
        }
      }
    };
    watch(computedPopupVisible, (value) => {
      if (value) {
        if (computedKeys.value.length > 0 && !activeNode.value) {
          const lastKey = computedKeys.value[computedKeys.value.length - 1];
          const node = leafOptionMap.get(lastKey);
          if (node) {
            setSelectedPath(node);
            setActiveNode(node);
          }
        }
      } else {
        if (computedKeys.value.length === 0) {
          setSelectedPath();
          setActiveNode();
        }
        handleInputValueChange("", "optionListHide");
      }
    });
    const handleClear = (e) => {
      e.stopPropagation();
      if (props.multiple) {
        const newValue = computedKeys.value.reduce((pre, key) => {
          const option = leafOptionMap.get(key);
          if (option == null ? void 0 : option.disabled) {
            pre.push(option);
          }
          return pre;
        }, []);
        updateValue(newValue);
      } else {
        updateValue();
      }
      handleInputValueChange("", "manual");
      emit("clear");
    };
    const showSearchPanel = computed(() => props.allowSearch && computedInputValue.value.length > 0);
    const handleFocus = (e) => {
      emit("focus", e);
    };
    const handleBlur = (e) => {
      emit("blur", e);
    };
    const {
      selectedPath,
      activeNode,
      displayColumns,
      setSelectedPath,
      setActiveNode,
      getNextActiveNode
    } = useSelectedPath(optionInfos, { filteredLeafOptions, showSearchPanel });
    const handleKeyDown = getKeyDownHandler(new Map([
      [
        CODE.ENTER,
        (e) => {
          if (computedPopupVisible.value) {
            if (activeNode.value) {
              handleClickOption(activeNode.value);
            }
          } else {
            handlePopupVisibleChange(true);
          }
        }
      ],
      [
        CODE.ESC,
        (e) => {
          handlePopupVisibleChange(false);
        }
      ],
      [
        CODE.ARROW_DOWN,
        (e) => {
          const activeNode2 = getNextActiveNode("next");
          setActiveNode(activeNode2);
          e == null ? void 0 : e.preventDefault();
        }
      ],
      [
        CODE.ARROW_UP,
        (e) => {
          const activeNode2 = getNextActiveNode("preview");
          setActiveNode(activeNode2);
          e == null ? void 0 : e.preventDefault();
        }
      ],
      [
        CODE.ARROW_RIGHT,
        (e) => {
          var _a, _b;
          if ((_a = activeNode.value) == null ? void 0 : _a.children) {
            setSelectedPath(activeNode.value);
            setActiveNode((_b = activeNode.value) == null ? void 0 : _b.children[0]);
          }
          e == null ? void 0 : e.preventDefault();
        }
      ],
      [
        CODE.ARROW_LEFT,
        (e) => {
          var _a, _b, _c;
          if ((_a = activeNode.value) == null ? void 0 : _a.parent) {
            setActiveNode((_b = activeNode.value) == null ? void 0 : _b.parent);
            setSelectedPath((_c = activeNode.value) == null ? void 0 : _c.parent);
          }
          e == null ? void 0 : e.preventDefault();
        }
      ]
    ]));
    const selectViewValue = computed(() => {
      var _a, _b, _c, _d;
      if (props.multiple) {
        const result = [];
        for (const key of computedKeys.value) {
          const option2 = leafOptionMap.get(key);
          if (option2) {
            const value = {
              value: key,
              label: (_b = (_a = props.formatLabel) == null ? void 0 : _a.call(props, option2.path)) != null ? _b : getOptionLabel(option2),
              closable: !option2.disabled,
              tagProps: option2.tagProps
            };
            result.push(value);
          }
        }
        return result;
      }
      const option = leafOptionMap.get(computedKeys.value[0]);
      if (!option) {
        return void 0;
      }
      return {
        value: option.key,
        label: (_d = (_c = props.formatLabel) == null ? void 0 : _c.call(props, option.path)) != null ? _d : getOptionLabel(option),
        closable: !(option == null ? void 0 : option.disabled),
        tagProps: option.tagProps
      };
    });
    return {
      optionInfos,
      computedKeys,
      filteredLeafOptions,
      selectedPath,
      activeNode,
      displayColumns,
      computedInputValue,
      computedPopupVisible,
      handleClear,
      setSelectedPath,
      setActiveNode,
      selectViewValue,
      handleInputValueChange,
      showSearchPanel,
      handlePopupVisibleChange,
      handleFocus,
      handleClickOption,
      handleBlur,
      handleRemove,
      handleKeyDown,
      totalLevel
    };
  }
});
function _sfc_render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_select_view = resolveComponent("select-view");
  const _component_cascader_search_panel = resolveComponent("cascader-search-panel");
  const _component_cascader_panel = resolveComponent("cascader-panel");
  const _component_trigger = resolveComponent("trigger");
  return openBlock(), createBlock(_component_trigger, mergeProps(_ctx.triggerProps, {
    trigger: "click",
    "popup-visible": _ctx.computedPopupVisible,
    position: "bl",
    disabled: _ctx.disabled,
    "popup-offset": 4,
    "auto-fit-popup-width": _ctx.showSearchPanel,
    "popup-container": _ctx.popupContainer,
    "prevent-focus": true,
    onPopupVisibleChange: _ctx.handlePopupVisibleChange
  }), {
    content: withCtx(() => [
      _ctx.showSearchPanel ? (openBlock(), createBlock(_component_cascader_search_panel, {
        key: 0,
        options: _ctx.filteredLeafOptions,
        "active-node": _ctx.activeNode,
        "computed-keys": _ctx.computedKeys,
        multiple: _ctx.multiple,
        onClickOption: _ctx.handleClickOption,
        onActiveChange: _ctx.setActiveNode
      }, null, 8, ["options", "active-node", "computed-keys", "multiple", "onClickOption", "onActiveChange"])) : (openBlock(), createBlock(_component_cascader_panel, {
        key: 1,
        "display-columns": _ctx.displayColumns,
        "selected-path": _ctx.selectedPath,
        "active-node": _ctx.activeNode,
        "computed-keys": _ctx.computedKeys,
        multiple: _ctx.multiple,
        "expand-trigger": _ctx.expandTrigger,
        "total-level": _ctx.totalLevel,
        onClickOption: _ctx.handleClickOption,
        onActiveChange: _ctx.setActiveNode,
        onPathChange: _ctx.setSelectedPath
      }, null, 8, ["display-columns", "selected-path", "active-node", "computed-keys", "multiple", "expand-trigger", "total-level", "onClickOption", "onActiveChange", "onPathChange"]))
    ]),
    default: withCtx(() => [
      createVNode(_component_select_view, mergeProps({
        "model-value": _ctx.selectViewValue,
        "input-value": _ctx.computedInputValue,
        disabled: _ctx.disabled,
        error: _ctx.error,
        multiple: _ctx.multiple,
        "allow-clear": _ctx.allowClear,
        "allow-search": _ctx.allowSearch,
        opened: _ctx.computedPopupVisible,
        placeholder: _ctx.placeholder
      }, _ctx.$attrs, {
        onInputValueChange: _ctx.handleInputValueChange,
        onClear: _ctx.handleClear,
        onFocus: _ctx.handleFocus,
        onBlur: _ctx.handleBlur,
        onRemove: _ctx.handleRemove,
        onKeydown: _ctx.handleKeyDown
      }), null, 16, ["model-value", "input-value", "disabled", "error", "multiple", "allow-clear", "allow-search", "opened", "placeholder", "onInputValueChange", "onClear", "onFocus", "onBlur", "onRemove", "onKeydown"])
    ]),
    _: 1
  }, 16, ["popup-visible", "disabled", "auto-fit-popup-width", "popup-container", "onPopupVisibleChange"]);
}
var _Cascader = /* @__PURE__ */ _export_sfc(_sfc_main, [["render", _sfc_render]]);
export { _Cascader as default };
